// Layout documents: which widget instances exist, how each is configured,
// and where each sits on the wall grid. Shared by Worker (seed/validation)
// and the app (rendering/editing).

export interface GridRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WidgetInstance {
  /** Stable instance id; two instances of one type have different ids. */
  id: string;
  type: string;
  /** Widget definition version the config was written for. */
  version: number;
  config: Record<string, unknown>;
  /** Position on the fitted wall grid. */
  wall: GridRect;
}

export interface LayoutGrid {
  cols: number;
  rows: number;
}

export interface LayoutBody {
  grid: LayoutGrid;
  items: WidgetInstance[];
}

export interface LayoutDoc extends LayoutBody {
  id: string;
  name: string;
  presetKey: string | null;
  sortOrder: number;
  revision: number;
  updatedAt: string;
}

/** 12×12 gives a slim hero band (2 rows ≈ 17%) and fine-grained card sizes. */
export const WALL_GRID: LayoutGrid = { cols: 12, rows: 12 };

/**
 * Order used when stacking widgets in a single column on narrow screens: top to
 * bottom, then left to right, except that a clock in the wall's top band leads, so
 * a phone opens on the time even where a tall map starts at the top-left corner.
 */
export function stackOrder(items: WidgetInstance[]): WidgetInstance[] {
  const lead = (it: WidgetInstance) => (it.type === "clock" && it.wall.y <= 1 ? 0 : 1);
  return [...items].sort((a, b) => lead(a) - lead(b) || a.wall.y - b.wall.y || a.wall.x - b.wall.x);
}

/** Clamp a rect into the grid so a bad save can never put a widget off-screen. */
export function clampRect(r: GridRect, grid: LayoutGrid): GridRect {
  const w = Math.max(1, Math.min(Math.round(r.w), grid.cols));
  const h = Math.max(1, Math.min(Math.round(r.h), grid.rows));
  const x = Math.max(0, Math.min(Math.round(r.x), grid.cols - w));
  const y = Math.max(0, Math.min(Math.round(r.y), grid.rows - h));
  return { x, y, w, h };
}

export function rectsOverlap(a: GridRect, b: GridRect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Find the first free spot for a w×h widget, scanning row by row.
 * Returns null when the wall is full.
 */
export function findFreeSpot(items: WidgetInstance[], w: number, h: number, grid: LayoutGrid): GridRect | null {
  for (let y = 0; y + h <= grid.rows; y++) {
    for (let x = 0; x + w <= grid.cols; x++) {
      const candidate = { x, y, w, h };
      if (!items.some((it) => rectsOverlap(it.wall, candidate))) return candidate;
    }
  }
  return null;
}

/** Structural validation for documents arriving from clients. Throws on invalid input. */
export function validateLayoutBody(input: unknown): LayoutBody {
  if (!input || typeof input !== "object") throw new Error("Layout must be an object");
  const body = input as Partial<LayoutBody>;
  const grid = body.grid ?? WALL_GRID;
  if (!Number.isInteger(grid.cols) || !Number.isInteger(grid.rows) || grid.cols < 1 || grid.cols > 24 || grid.rows < 1 || grid.rows > 24) {
    throw new Error("Invalid grid");
  }
  if (!Array.isArray(body.items)) throw new Error("Layout items must be an array");
  if (body.items.length > 40) throw new Error("Too many widgets");
  const seen = new Set<string>();
  const items = body.items.map((raw) => {
    const it = raw as Partial<WidgetInstance>;
    if (typeof it.id !== "string" || !/^[\w-]{1,64}$/.test(it.id)) throw new Error("Invalid widget id");
    if (seen.has(it.id)) throw new Error("Duplicate widget id");
    seen.add(it.id);
    if (typeof it.type !== "string" || !/^[\w-]{1,40}$/.test(it.type)) throw new Error("Invalid widget type");
    const config = it.config && typeof it.config === "object" && !Array.isArray(it.config) ? it.config : {};
    if (JSON.stringify(config).length > 8_000) throw new Error("Widget config too large");
    if (!it.wall) throw new Error("Widget position missing");
    return {
      id: it.id,
      type: it.type,
      version: Number.isInteger(it.version) ? (it.version as number) : 1,
      config,
      wall: clampRect(it.wall, grid),
    };
  });
  return { grid: { cols: grid.cols, rows: grid.rows }, items };
}
